import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Spinner, Clock, Eye, PencilSimple, CheckCircle, XCircle,
  ArrowClockwise, CaretUp, CaretDown, MagnifyingGlass, PaperPlaneTilt, DownloadSimple,
} from '@phosphor-icons/react';
import { useSubmissionStore } from '../../stores/submissionStore';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../lib/api-client';
import JSZip from 'jszip';
import { useAssetStore } from '../../stores/assetStore';
import { useDriveStore } from '../../stores/driveStore';
import SubmitReviewDialog from '../review/SubmitReviewDialog';
import ConfirmDialog from '../shared/ConfirmDialog';
import type { Submission, SubmissionStatus } from '@spine-workspace/shared';

const STATUS_CONFIG: Record<SubmissionStatus, { label: string; color: string; icon: React.ReactNode }> = {
  waiting_for_review: { label: 'Waiting',      color: 'text-amber-400 bg-amber-400/10 border-amber-400/20',        icon: <Clock size={10} weight="fill" /> },
  in_review:          { label: 'In Review',     color: 'text-blue-400 bg-blue-400/10 border-blue-400/20',          icon: <Eye size={10} weight="fill" /> },
  need_update:        { label: 'Needs Update',  color: 'text-red-400 bg-red-400/10 border-red-400/20',             icon: <PencilSimple size={10} weight="fill" /> },
  approved:           { label: 'Approved',      color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20', icon: <CheckCircle size={10} weight="fill" /> },
  cancelled:          { label: 'Cancelled',     color: 'text-text-tertiary bg-bg-input border-border',             icon: <XCircle size={10} weight="fill" /> },
};

const STATUS_ORDER: SubmissionStatus[] = ['waiting_for_review', 'in_review', 'need_update', 'approved', 'cancelled'];

type SortKey = 'assetName' | 'submitterName' | 'status' | 'submittedAt' | 'resubmissionCount';
type SortDir = 'asc' | 'desc';

function timeAgo(iso: string) {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function Submissions() {
  const { submissions, loading, error, fetchSubmissions, submitForReview } = useSubmissionStore();
  const isAdmin = useAuthStore(s => s.user?.isAdmin ?? false);
  const currentUserId = useAuthStore(s => s.user?.id);
  const { assets } = useAssetStore();
  const { savedFolders } = useDriveStore();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<SubmissionStatus | 'all'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('submittedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Resubmit dialog
  const [resubmitTarget, setResubmitTarget] = useState<Submission | null>(null);

  useEffect(() => { fetchSubmissions(); }, []);

  const counts: Record<string, number> = Object.fromEntries(
    STATUS_ORDER.map(k => [k, submissions.filter(s => s.status === k).length])
  );

  const rows = useMemo(() => {
    let list = submissions;
    if (statusFilter !== 'all') list = list.filter(s => s.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        (s.submissionName ?? s.assetName ?? '').toLowerCase().includes(q) ||
        (s.driveFolderName ?? '').toLowerCase().includes(q) ||
        (s.submitterName ?? '').toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a, b) => {
      let va: string | number, vb: string | number;
      if (sortKey === 'status') {
        va = STATUS_ORDER.indexOf(a.status);
        vb = STATUS_ORDER.indexOf(b.status);
      } else if (sortKey === 'resubmissionCount') {
        va = a.resubmissionCount; vb = b.resubmissionCount;
      } else if (sortKey === 'submittedAt') {
        va = a.submittedAt; vb = b.submittedAt;
      } else if (sortKey === 'assetName') {
        va = (a.submissionName ?? a.assetName ?? a.driveFolderName ?? '').toLowerCase();
        vb = (b.submissionName ?? b.assetName ?? b.driveFolderName ?? '').toLowerCase();
      } else {
        va = (a.submitterName ?? '').toLowerCase();
        vb = (b.submitterName ?? '').toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [submissions, statusFilter, search, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <CaretDown size={10} className="text-text-tertiary/40" />;
    return sortDir === 'asc'
      ? <CaretUp size={10} className="text-accent" />
      : <CaretDown size={10} className="text-accent" />;
  }

  function Th({ col, label, className = '' }: { col: SortKey; label: string; className?: string }) {
    return (
      <th
        onClick={() => toggleSort(col)}
        className={`px-3 py-2.5 text-left text-[11px] font-mono text-text-tertiary uppercase tracking-wide cursor-pointer select-none hover:text-text-primary transition-colors ${className}`}
      >
        <span className="flex items-center gap-1">{label}<SortIcon col={col} /></span>
      </th>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search asset or submitter…"
            className="w-full pl-7 pr-3 py-1.5 bg-bg-input border border-border rounded text-xs font-sans text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent transition-colors"
          />
        </div>

        {/* Status filter pills */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-2.5 py-1 rounded text-[11px] font-mono transition-colors ${
              statusFilter === 'all' ? 'bg-accent/15 text-accent' : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
            }`}
          >
            All {submissions.length > 0 && <span className="ml-0.5 opacity-70">{submissions.length}</span>}
          </button>
          {STATUS_ORDER.map(s => {
            const cfg = STATUS_CONFIG[s];
            const active = statusFilter === s;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded border text-[11px] font-mono transition-colors ${
                  active ? cfg.color : 'border-transparent text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
                }`}
              >
                {cfg.icon}
                {cfg.label}
                {counts[s] > 0 && <span className="ml-0.5 opacity-70">{counts[s]}</span>}
              </button>
            );
          })}
        </div>

        <button
          onClick={fetchSubmissions}
          className="ml-auto w-8 h-8 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-colors"
          title="Refresh"
        >
          <ArrowClockwise size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Table */}
      {loading && submissions.length === 0 ? (
        <div className="flex justify-center py-20">
          <Spinner size={20} className="animate-spin text-text-tertiary" />
        </div>
      ) : error ? (
        <div className="text-red-400 text-sm text-center py-10">{error}</div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bg-input border-b border-border">
              <tr>
                <Th col="assetName"         label="Submission" className="w-[28%]" />
                <Th col="status"            label="Status"     className="w-[12%]" />
                <Th col="submitterName"     label="Submitter"  className="w-[12%]" />
                <Th col="submittedAt"       label="Submitted"  className="w-[11%]" />
                <Th col="resubmissionCount" label="Ver."       className="w-[5%]"  />
                <th className="px-3 py-2.5 w-[32%]" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-14 text-text-tertiary text-xs font-sans">
                    No submissions match your filter
                  </td>
                </tr>
              ) : rows.map(sub => (
                <SubmissionRow
                  key={sub.id}
                  submission={sub}
                  isAdmin={isAdmin}
                  isOwn={sub.submittedBy === currentUserId}
                  onOpen={() => sub.reviewSceneId && navigate(`/scene/${sub.reviewSceneId}/edit`)}
                  onResubmit={() => setResubmitTarget(sub)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Resubmit dialog */}
      {resubmitTarget && (() => {
        const sub = resubmitTarget;
        const folder = savedFolders.find(f => f.folderId === sub.driveFolderId);
        const folderAssets = folder
          ? folder.assetIds.map(id => assets.find(a => a.id === id)).filter(Boolean) as typeof assets
          : assets.filter(a => a.id === sub.assetId);
        return (
          <SubmitReviewDialog
            open={true}
            onClose={() => setResubmitTarget(null)}
            folderId={sub.driveFolderId}
            folderName={sub.driveFolderName ?? ''}
            assets={folderAssets}
            existingSubmission={sub}
            onConfirm={async ({ name, note }) => {
              await submitForReview({
                driveFolderId: sub.driveFolderId,
                driveFolderName: sub.driveFolderName ?? undefined,
                assetId: sub.assetId,
                name,
                note,
              });
              await fetchSubmissions();
            }}
          />
        );
      })()}
    </div>
  );
}

function SubmissionRow({
  submission: sub,
  isAdmin,
  isOwn,
  onOpen,
  onResubmit,
}: {
  submission: Submission;
  isAdmin: boolean;
  isOwn: boolean;
  onOpen: () => void;
  onResubmit: () => void;
}) {
  const { updateStatus } = useSubmissionStore();
  const [acting, setAct] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const cfg = STATUS_CONFIG[sub.status] ?? STATUS_CONFIG['waiting_for_review'];
  const isCancelled = sub.status === 'cancelled';

  async function act(status: SubmissionStatus, e: React.MouseEvent) {
    e.stopPropagation();
    setAct(true);
    try { await updateStatus(sub.id, status); }
    finally { setAct(false); }
  }

  async function handleRestore(e: React.MouseEvent) {
    e.stopPropagation();
    setAct(true);
    try { await updateStatus(sub.id, 'waiting_for_review'); }
    finally { setAct(false); }
  }

  async function handleDownload(e: React.MouseEvent) {
    e.stopPropagation();
    if (downloading || !sub.assetId) return;
    setDownloading(true);
    try {
      const { asset } = await api.get<{ asset: { jsonUrl: string; atlasUrl: string; pngUrls: string[] } }>(`/api/assets/${sub.assetId}`);
      const urls = [asset.jsonUrl, asset.atlasUrl, ...asset.pngUrls];
      const zip = new JSZip();
      await Promise.all(urls.map(async url => {
        const res = await fetch(url);
        if (!res.ok) return;
        const blob = await res.blob();
        const filename = url.split('/').pop()?.split('?')[0] ?? 'file';
        zip.file(filename, blob);
      }));
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipName = (sub.submissionName ?? sub.assetName ?? 'asset').replace(/[^a-zA-Z0-9_-]/g, '_') + '.zip';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = zipName;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <tr
        onClick={onOpen}
        className={`hover:bg-bg-hover transition-colors cursor-pointer group ${isCancelled ? 'opacity-50' : ''}`}
      >
        {/* Submission name */}
        <td className="px-3 py-2.5">
          <span className="font-sans font-medium text-text-primary group-hover:text-accent transition-colors truncate block max-w-xs" title={sub.submissionName ?? sub.assetName ?? undefined}>
            {sub.submissionName ?? sub.assetName ?? sub.driveFolderName ?? sub.driveFolderId.slice(0, 12)}
          </span>
          {sub.submitterNote && (
            <span className="text-[10px] font-sans text-text-tertiary italic truncate block" title={sub.submitterNote}>
              {sub.submitterNote}
            </span>
          )}
        </td>

        {/* Status */}
        <td className="px-3 py-2.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono font-semibold ${cfg.color}`}>
            {cfg.icon}{cfg.label}
          </span>
        </td>

        {/* Submitter */}
        <td className="px-3 py-2.5 text-xs font-sans text-text-secondary">
          {sub.submitterName ?? '—'}
        </td>

        {/* Submitted */}
        <td className="px-3 py-2.5 text-xs font-mono text-text-tertiary" title={sub.submittedAt}>
          {timeAgo(sub.submittedAt)}
        </td>

        {/* Version */}
        <td className="px-3 py-2.5 text-xs font-mono text-text-tertiary text-center">
          v{sub.resubmissionCount + 1}
        </td>

        {/* Actions */}
        <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
          <div className="flex gap-1 justify-end flex-wrap">

            {/* Download */}
            {sub.assetId && (
              <button
                disabled={downloading}
                onClick={handleDownload}
                title="Download asset files"
                className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-sans rounded bg-bg-hover text-text-secondary hover:text-text-primary disabled:opacity-50 transition-colors whitespace-nowrap"
              >
                {downloading
                  ? <Spinner size={9} className="animate-spin" />
                  : <DownloadSimple size={9} />
                }
                Download
              </button>
            )}

            {/* Cancelled → Restore */}
            {isCancelled && (isOwn || isAdmin) && (
              <button disabled={acting} onClick={handleRestore}
                className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-sans rounded bg-bg-hover text-text-secondary hover:text-text-primary disabled:opacity-50 transition-colors whitespace-nowrap">
                <ArrowClockwise size={9} />Restore
              </button>
            )}

            {!isCancelled && (
              <>
                {/* Resubmit — submitter or admin */}
                {(isOwn || isAdmin) && (
                  <button onClick={e => { e.stopPropagation(); onResubmit(); }}
                    className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-sans rounded bg-accent/10 text-accent hover:bg-accent/20 transition-colors whitespace-nowrap">
                    <PaperPlaneTilt size={9} />Resubmit
                  </button>
                )}

                {/* Admin: status transitions */}
                {isAdmin && sub.status === 'waiting_for_review' && (
                  <button disabled={acting} onClick={e => act('in_review', e)}
                    className="px-2 py-0.5 text-[10px] font-sans rounded bg-blue-400/10 text-blue-400 hover:bg-blue-400/20 disabled:opacity-50 transition-colors whitespace-nowrap">
                    Start Review
                  </button>
                )}
                {isAdmin && sub.status === 'in_review' && (
                  <>
                    <button disabled={acting} onClick={e => act('approved', e)}
                      className="px-2 py-0.5 text-[10px] font-sans rounded bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20 disabled:opacity-50 transition-colors">
                      Approve
                    </button>
                    <button disabled={acting} onClick={e => act('need_update', e)}
                      className="px-2 py-0.5 text-[10px] font-sans rounded bg-red-400/10 text-red-400 hover:bg-red-400/20 disabled:opacity-50 transition-colors whitespace-nowrap">
                      Needs Update
                    </button>
                  </>
                )}
                {isAdmin && sub.status === 'approved' && (
                  <button disabled={acting} onClick={e => act('in_review', e)}
                    className="px-2 py-0.5 text-[10px] font-sans rounded bg-bg-hover text-text-secondary hover:text-text-primary disabled:opacity-50 transition-colors whitespace-nowrap">
                    Recall
                  </button>
                )}
                {isAdmin && sub.status === 'need_update' && (
                  <button disabled={acting} onClick={e => act('in_review', e)}
                    className="px-2 py-0.5 text-[10px] font-sans rounded bg-blue-400/10 text-blue-400 hover:bg-blue-400/20 disabled:opacity-50 transition-colors whitespace-nowrap">
                    Re-open
                  </button>
                )}

                {/* Cancel — submitter or admin */}
                {(isOwn || isAdmin) && (
                  <button disabled={acting} onClick={e => { e.stopPropagation(); setConfirmCancel(true); }}
                    className="px-2 py-0.5 text-[10px] font-sans rounded bg-bg-hover text-text-tertiary hover:text-red-400 hover:bg-red-400/10 disabled:opacity-50 transition-colors whitespace-nowrap">
                    Cancel
                  </button>
                )}
              </>
            )}

            {acting && <Spinner size={11} className="animate-spin text-text-tertiary self-center" />}
          </div>
        </td>
      </tr>

      <ConfirmDialog
        open={confirmCancel}
        title="Cancel submission?"
        message={`"${sub.submissionName ?? sub.assetName ?? 'This submission'}" will be marked as cancelled. You can restore it later.`}
        confirmLabel="Cancel Submission"
        danger
        onCancel={() => setConfirmCancel(false)}
        onConfirm={async () => {
          setConfirmCancel(false);
          setAct(true);
          try { await updateStatus(sub.id, 'cancelled'); }
          finally { setAct(false); }
        }}
      />
    </>
  );
}
